import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { cn } from '@restworld/utils-common';
import { Heading, Spinner } from '@restworld/ui-ds-base';

import { fullScreenTogglerAtom, getToggleFullScreenAtom, getMapRefAtom } from '../../job-explorer-map.atoms';
import { useIsB2BAdvertisement } from '../job-explorer-map';

const CustomMapWrapper = ({
  toggleFullScreen,
  className,
  ...props
}: {
  toggleFullScreen?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <>
    <style>{`
      .custom-map-wrapper .maplibregl-map {
        height: 100%; width: 100%;
        box-shadow: inset 0 0 16px 0 rgba(0, 0, 26, 0.15);
      }
      .custom-map-wrapper .maplibregl-ctrl-top-right .maplibregl-ctrl { margin: 24px 24px 0 0; }
      .custom-map-wrapper .maplibregl-ctrl-bottom-right { position: fixed; bottom: 20%; }
      @media (min-width: 1024px) { .custom-map-wrapper .maplibregl-ctrl-bottom-right { position: absolute; bottom: 0; right: 0; } }
      .custom-map-wrapper .maplibregl-ctrl button.maplibregl-ctrl-zoom-in .maplibregl-ctrl-icon { background-image: url(https://worker.restworld.it/utils-assets/map-plus-icon.svg); }
      .custom-map-wrapper .maplibregl-ctrl button.maplibregl-ctrl-zoom-out .maplibregl-ctrl-icon { background-image: url(https://worker.restworld.it/utils-assets/map-minus-icon.svg); }
      .custom-map-wrapper .maplibregl-ctrl button.maplibregl-ctrl-geolocate .maplibregl-ctrl-icon { background-image: url(https://worker.restworld.it/utils-assets/map-geolocate-icon.svg); }
      .custom-map-wrapper .maplibregl-ctrl-group button { width: 40px; height: 40px; }
    `}</style>
    <div
      className={cn(
        'custom-map-wrapper relative w-full h-[calc(100svh-9.5rem)] lg:h-[calc(100vh-11.2rem)]',
        toggleFullScreen && 'lg:h-[calc(-11.093rem+100vh)]',
        className
      )}
      {...props}
    />
  </>
);
const MapSpinner = () => (
  <div className="absolute bottom-[16px] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 w-[88px] h-[48px] flex justify-center items-center bg-white border rounded-lg border-[#E5E7EB]">
    <Spinner color="primary" size="sm" />
  </div>
);
const FullScreenCustomControl = () => {
  const mapRef = useAtomValue(getMapRefAtom);
  const toggleFullScreen = useAtomValue(getToggleFullScreenAtom);
  const fullScreenToggler = useSetAtom(fullScreenTogglerAtom);
  const { t } = useTranslation('common');
  const isB2BAdvertisement = useIsB2BAdvertisement();
  return (
    <div
      onClick={() => {
        mapRef.current?.triggerRepaint();
        fullScreenToggler();
      }}
      className={cn(
        'absolute top-4 right-[72px] h-[40px] max-w-[6rem] bg-white border border-gray-200 shadow-lg p-2 rounded-lg cursor-pointer hover:bg-gray-200',
        isB2BAdvertisement && 'top-16 right-[72px]'
      )}
    >
      <div className="flex flex-row gap-2">
        {toggleFullScreen ? <MapUnZoomIcon /> : <MapZoomIcon />}
        <Heading size={6} weight="medium" className="cursor-pointer">
          {toggleFullScreen ? t('reduce') : t('expand')}
        </Heading>
      </div>
    </div>
  );
};
const MapZoomIcon = ({ stroke = '#111928', ...rest }: { stroke?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" {...rest}>
    <path
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M20 20h-4M4 8V4v4zm0-4h4-4zm0 0l5 5-5-5zm16 4V4v4zm0-4h-4 4zm0 0l-5 5 5-5zM4 16v4-4zm0 4h4-4zm0 0l5-5-5 5zm16 0l-5-5 5 5zm0 0v-4 4z"
    />
  </svg>
);
const MapUnZoomIcon = ({ stroke = '#111928', ...rest }: { stroke?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
    <path
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M9 5v4m0 0H5m4 0L4 4m11 1v4m0 0h4m-4 0l5-5M9 19v-4m0 0H5m4 0l-5 5m11-5l5 5m-5-5v4m0-4h4"
    />
  </svg>
);

export { MapSpinner, FullScreenCustomControl, CustomMapWrapper };
